import { clientConnector } from '@games/socket-client'
import type { socketParamsConfig } from './clientConnector/types'
import { logger } from "@games/logger-client";

import { Unity, useUnityContext } from "react-unity-webgl";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { accessTokens } from "./accessTokens";

import ClientConnectorTestPage from "./ClientConnectorTestPage";
import { extendSocketWithUser } from "./socket/user";
import { extendSocketWithRoom } from "./socket/room";
import { extendSocketWithSettings } from "./socket/globalSettings";
import { extendSocketWithStats } from "./socket/stats";
import { extendSocketWithChat } from "./socket/chat";
import { extendSocketWithGame } from "./socket/game";
import { extendSocketWithBattleInitData } from "./socket/battleInitData";


declare global {
  interface Window {
    sendMessage?: (gameObject: string, method: string, data?: any) => any;
    Telegram?: any;
    clientConnector?: typeof clientConnector;
    socketParams?: socketParamsConfig;
  }
}

const UnityComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const accessTokenIndex = searchParams.get("token");
  const buildFolder = searchParams.get("build") || "Build";

  const accessToken = accessTokenIndex
    ? accessTokens[parseInt(accessTokenIndex, 10)] ?? accessTokens[0]
    : "здесь-должен-оказаться-токен-клиента";

  if (buildFolder === "Fake") {
    return <ClientConnectorTestPage />;
  }

  const [devicePixelRatio, setDevicePixelRatio] = useState(
    window.devicePixelRatio
  );
  useEffect(() => {
    logger.componentMount("UnityComponent");
    return () => {
      logger.componentUnmount("UnityComponent");
    };
  }, []);

  // Unity context paths
  const loaderUrl = `UnityBuild/${buildFolder}/Build/unityLoader.js`;
  const dataUrl = `UnityBuild/${buildFolder}/Build/content.data.unityweb`;
  const frameworkUrl = `UnityBuild/${buildFolder}/Build/framework.js.unityweb`;
  const codeUrl = `UnityBuild/${buildFolder}/Build/content.wasm.unityweb`;
  const streamingAssetsUrl = `UnityBuild/${buildFolder}/StreamingAssets`;

  // Log Unity context configuration
  useEffect(() => {
    logger.unity("Unity context configuration:", {
      buildFolder,
      loaderUrl,
      dataUrl,
      frameworkUrl,
      codeUrl,
      streamingAssetsUrl
    });
  }, [buildFolder, loaderUrl, dataUrl, frameworkUrl, codeUrl, streamingAssetsUrl]);

  const {
    unityProvider,
    isLoaded,
    sendMessage: unitySendMessage,
    addEventListener,
    removeEventListener,
    unload,
  } = useUnityContext({
    loaderUrl,
    dataUrl,
    frameworkUrl,
    codeUrl,
    streamingAssetsUrl,
  });

  useEffect(() => {
    const updateDevicePixelRatio = () => {
      setDevicePixelRatio(window.devicePixelRatio);
    };

    const mediaMatcher = window.matchMedia(
      `screen and (resolution: ${devicePixelRatio}dppx)`
    );

    mediaMatcher.addEventListener("change", updateDevicePixelRatio);

    return () => {
      mediaMatcher.removeEventListener("change", updateDevicePixelRatio);
    };
  }, [devicePixelRatio]);

  useEffect(() => {
    logger.info("🚀 Setting up Unity integration");
    logger.unity("Using access token:", accessToken);
    logger.unity("Using build folder:", buildFolder);

    const methodHandlers: Record<string, (data?: any) => any> = {
      NavigateBack: () => {
        logger.navigation("NavigateBack called");
        navigate("/main");
      },
      GetAccessToken: () => {
        logger.unity("GetAccessToken called, returning:", accessToken);
        return accessToken;
      },
      GetInitData: () => {
        const initData = window.Telegram?.WebApp?.initData || "";

        logger.telegram("GetInitData called, returning:", initData);
        return initData;
      },
      GetLanguage: () => {
        const language =
          window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code || "";
        logger.telegram("GetLanguage called, returning:", language);
        return language;
      },
    };
    logger.telegram("Telegram WebApp init data:", window.Telegram?.WebApp?.initData);
    window.sendMessage = (
      gameObject: string,
      method: string,
      data?: any
    ): any => {
      logger.unity(
        `sendMessage called with gameObject: ${gameObject}, method: ${method}, data:`,
        data
      );
      if (gameObject === "ReactBridge" && methodHandlers[method]) {
        const result = methodHandlers[method](data);
        if (isLoaded) {
          logger.unity(`Sending result to Unity - ${method}:`, result);
          switch (method) {
            case "GetAccessToken":
              unitySendMessage(
                "TelegramProvider",
                "ReceiveAccessToken",
                String(result)
              );
              break;
            case "GetInitData":
              unitySendMessage(
                "TelegramProvider",
                "ReceiveInitData",
                String(result)
              );
              break;
            case "GetLanguage":
              unitySendMessage(
                "TelegramProvider",
                "ReceiveLanguage",
                String(result)
              );
              break;
          }
        } else {
          logger.warn("Unity is not loaded yet, cannot send message");
        }
        return result;
      } else {
        logger.warn(`Unknown method: ${method}`);
      }
    };

    const handleUnityLoaded = () => {
      logger.info("🎉 Unity is fully loaded, sending ReactReady signal");
      unitySendMessage("TelegramProvider", "ReactReady", "");
    };

    if (isLoaded) {
      handleUnityLoaded();
    } else {
      addEventListener("loaded", handleUnityLoaded);
    }

    return () => {
      logger.unity("Cleaning up Unity integration");
      removeEventListener("loaded", handleUnityLoaded);
      delete window.sendMessage;
    };
  }, [
    navigate,
    accessToken,
    buildFolder,
    isLoaded,
    unitySendMessage,
    addEventListener,
    removeEventListener,
    unityProvider,
  ]);

  useEffect(() => {

  // Здесь указываем адрес сервера
  const socketUrl =
    process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_SOCKET_URL || `https://${window.location.hostname}`
      : window.location.origin; // 3000 → proxy → 3999

  const socketParams = {
    server: socketUrl,
    msgPack: true,
    params: {
      auth: { token: accessToken },
      transports: ["polling", "websocket"],
    },
  };
  window.socketParams = socketParams

  window.clientConnector = clientConnector;
  // Инициализируем расширения приложения
  extendSocketWithSettings()
  extendSocketWithStats()
  extendSocketWithUser()
  extendSocketWithRoom()
  extendSocketWithChat()
  extendSocketWithGame()
  extendSocketWithBattleInitData()
  logger.socket("Socket factory created and exposed to window.clientConnector");


  return () => {
    if (window.clientConnector?.isInitialized()) {
      logger.socket("Destroying socket connection on component unmount");
      window.clientConnector.destroy();
    }

    if (window.clientConnector) {
      delete window.clientConnector;
      logger.socket("Socket factory removed from window");
    }
  };
}, []);

// Модифицируем очистку Unity при размонтировании компонента
useEffect(() => {
  return () => {
    if (isLoaded) {
      logger.unity("Unloading Unity instance");
      try {
        unload();
      } catch (error) {
        logger.error("Error during Unity unload:", error);
      }
    } else {
      logger.unity("Unity not loaded, skipping unload");
    }
  };
}, [unload, isLoaded]);

return (
  <div style={{ width: "100%", height: "100vh", margin: 0, padding: 0 }}>
    <Unity
      unityProvider={unityProvider}
      style={{ width: "100%", height: "100%" }}
      devicePixelRatio={devicePixelRatio}
    />
  </div>
);
};

export default UnityComponent;
